import { Injectable } from '@angular/core';
import { ServiceService } from '../../../gen/syncui';
import { AuthService } from './auth.service';

export interface MetadataResult {
  success: boolean;
  error?: string;
  statusCode?: number;
  details?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  constructor(
    private serviceService: ServiceService,
    private authService: AuthService
  ) {}

  async getMetadata(): Promise<boolean> {
    console.log('MetadataService: Calling getMetadata endpoint for authentication verification...');

    return new Promise((resolve) => {
      this.serviceService.serviceGetMetadata().subscribe({
        next: (response) => {
          console.log('MetadataService: Metadata call successful, user is authenticated');
          resolve(true);
        },
        error: (error) => {
          console.error('MetadataService: Metadata call failed:', error);

          // Check if it's a 401 Unauthorized response
          if (error.status === 401) {
            console.log('MetadataService: Received 401 Unauthorized, user needs to authenticate');
            // Don't handle redirect here - let the AuthGuard handle it
            // This prevents infinite loops and ensures proper flow
            resolve(false);
          } else if (error.status === 403) {
            console.log('MetadataService: Received 403 Forbidden, user is authenticated but not authorized for metadata');
            // User is authenticated but not authorized for this specific resource
            // Still allow navigation to FID picker page
            resolve(true);
          } else {
            console.log('MetadataService: Non-401/403 error, treating as authentication failure');
            resolve(false);
          }
        }
      });
    });
  }

  async getMetadataWithDetails(): Promise<MetadataResult> {
    console.log('MetadataService: Calling getMetadata endpoint for detailed verification...');

    return new Promise((resolve) => {
      this.serviceService.serviceGetMetadata().subscribe({
        next: (response) => {
          console.log('MetadataService: Metadata call successful, user is authenticated');
          resolve({ success: true });
        },
        error: (error) => {
          console.error('MetadataService: Metadata call failed:', error);
          console.error('MetadataService: Error details:', JSON.stringify(error, null, 2));
          console.error('MetadataService: Error error property:', error.error);

          const errorDetails = JSON.stringify(error, null, 2);

          // Check if it's a 401 Unauthorized response
          if (error.status === 401) {
            console.log('MetadataService: Received 401 Unauthorized, user needs to authenticate');
            resolve({ 
              success: false, 
              error: error.error?.message || error.error?.detail || 'Authentication required. Please log in again.',
              statusCode: 401,
              details: errorDetails
            });
          } else if (error.status === 403) {
            console.log('MetadataService: Received 403 Forbidden, user is authenticated but not authorized for metadata');
            resolve({ 
              success: true, 
              error: error.error?.message || error.error?.detail || 'Access restricted for this resource.',
              statusCode: 403,
              details: errorDetails
            });
          } else {
            console.log('MetadataService: Non-401/403 error, treating as authentication failure');
            resolve({ 
              success: false, 
              error: `Service error: ${error.error?.message || error.error?.detail || error.message || 'Unknown error'}`,
              statusCode: error.status,
              details: errorDetails
            });
          }
        }
      });
    });
  }

}

